import React from 'react';
import { Redirect, Route, Switch } from 'react-router';

import { config } from 'src/config';
import { AIDesigner } from 'src/modules/aiDesigner';
import { Layout } from 'src/modules/app/components/Layout';
import { RedirectWithSession } from 'src/modules/app/components/RedirectWithSession';
import { DesignStudio } from 'src/modules/designStudio';
import { HomeownerApp } from 'src/modules/homeownerApp';
import { Layout as HomeownerAppLayout } from 'src/modules/homeownerApp/components/Layout';
import { Internal } from 'src/modules/internal';
import { Login } from 'src/modules/login';
import { NewConstructionUpload } from 'src/modules/newConstructionUpload';
import { NotFound } from 'src/modules/notFound';
import { PaintMeasurements } from 'src/modules/paintMeasurements';
import { Properties } from 'src/modules/properties';
import { PropertyDetails } from 'src/modules/propertyDetails';
import { Renderings } from 'src/modules/renderings';
import { Activate } from 'src/modules/renderings/components/Activate';
import { Layout as RenderingsLayout } from 'src/modules/renderings/components/Layout';
import { SignUp } from 'src/modules/signUp';
import { Mobile } from 'src/modules/signUp/components/Mobile';
import { SomethingWrong } from 'src/modules/somethingWrong';
import { SkipSSR } from 'src/shared/components/SSR/SkipSSR';
import { useLocation } from 'src/shared/hooks';
import { AuthenticatedLayoutContainer } from 'src/shared/layouts/AuthenticatedLayout/AuthenticatedLayout';
import { Provider as ToastProvider } from 'src/shared/providers/Toaster';
import { Route as Routes } from 'src/shared/route';

import { ProjectRoute } from './ProjectRoute';

const Router = () => (
  <Switch>
    {/* Design Studio paths */}
    <ProjectRoute
      dsn={config.SENTRY_DESIGN_STUDIO_DSN}
      layoutWrapper={ToastProvider}
      path={Routes.DESIGN_STUDIO.path}
    >
      <Route path={Routes.DESIGN_STUDIO.path}>
        <DesignStudio />
      </Route>
    </ProjectRoute>
    <ProjectRoute
      dsn={config.SENTRY_RENDERING_HUB_DSN}
      layoutWrapper={ToastProvider}
      path={Routes.RENDERINGS_INTERACTIVE.path}
    >
      <DesignStudio />
    </ProjectRoute>
    <ProjectRoute
      dsn={config.SENTRY_RENDERING_HUB_DSN}
      layoutWrapper={RenderingsLayout}
      path={[Routes.RENDERINGS_SHARED.path, Routes.RENDERINGS_HUB.path]}
    >
      <Renderings />
    </ProjectRoute>
    <ProjectRoute
      path={[Routes.AI_DESIGNER.path, Routes.AI_DESIGNER_SHARE.path]}
    >
      <AIDesigner />
    </ProjectRoute>
    <ProjectRoute
      layoutWrapper={HomeownerAppLayout}
      path={[
        Routes.HOMEOWNER_APP_ORIGINAL.path,
        Routes.HOMEOWNER_APP_HOME.path,
        Routes.HOMEOWNER_APP_FAVORITES.path,
        Routes.HOMEOWNER_APP_SHARE.path,
      ]}
    >
      <HomeownerApp />
    </ProjectRoute>
    <ProjectRoute
      layoutWrapper={AuthenticatedLayoutContainer}
      path={[Routes.PROPERTY_DETAILS.path]}
    >
      <PropertyDetails />
    </ProjectRoute>
    <ProjectRoute
      layoutWrapper={AuthenticatedLayoutContainer}
      path={[Routes.PROPERTIES.path]}
    >
      <SkipSSR>
        <Properties />
      </SkipSSR>
    </ProjectRoute>
    <ProjectRoute path={[Routes.NEW_CONSTRUCTION_UPLOAD.path]}>
      <SkipSSR>
        <NewConstructionUpload />
      </SkipSSR>
    </ProjectRoute>
    <ProjectRoute path={[Routes.PAINT_MEASUREMENTS_SHARED.path]}>
      <PaintMeasurements />
    </ProjectRoute>
    <ProjectRoute
      layoutWrapper={AuthenticatedLayoutContainer}
      path={[Routes.PAINT_MEASUREMENTS.path]}
    >
      <PaintMeasurements />
    </ProjectRoute>
    <ProjectRoute dsn={config.SENTRY_DSN} layoutWrapper={Layout}>
      <Route path={Routes.RENDERINGS_USER_ACTIVATE.path}>
        <RedirectWithSession>
          <Activate />
        </RedirectWithSession>
      </Route>
      {/* Ideally we could get Beacon to update this after we've migrated away
      from the WordPress-based `/login` and the `/beacon_return` in `website`,
      but I'm not sure if it's worth the trouble */}
      <Redirect
        from="/beacon_return"
        to={Routes.LOGIN_BEACON_SUCCESS.path + useLocation().search}
      />
      <Route path={Routes.LOGIN.path}>
        <Login />
      </Route>
      <Route path={Routes.SIGNUP_MOBILE.path}>
        <Mobile />
      </Route>
      <Route path={Routes.SIGN_UP.path}>
        <SignUp />
      </Route>
      <Route path={Routes.INTERNAL.path}>
        <Internal />
      </Route>
      <Route exact path={Routes.ROOT.path}>
        <Redirect to={Routes.LOGIN.path} />
      </Route>
      {/* Banned devices */}
      <Route path={Routes.SOMETHING_WRONG.path}>
        <SomethingWrong />
      </Route>
      <Route path="*">
        <NotFound />
      </Route>
    </ProjectRoute>
  </Switch>
);

export { Router };
